import axios, { AxiosError, AxiosInstance } from 'axios';
import { getAuth } from './auth';
import { setBearerAuthorization } from './bearerAuthorization';
import { notifySessionExpired } from './sessionExpiry';
import {
  AuthoredRuleRevision,
  AuthoredRuleRevisionListResponse,
  GrantRevocationResult,
  RuleArmRequest,
  RuleDeadLetter,
  RuleDeadLetterPage,
  RuleDetail,
  RuleListResponse,
  RuleRateCeiling,
  RuleRateCeilingRequest,
  RuleSimulationReport,
  RuleUntripResult,
  TenantKillSwitchView,
} from '../types/rules';

/** What an operational Rules API failure said: HTTP status, `error` code and correlation id. */
export interface RuleApiFailure {
  status?: number;
  code?: string;
  correlationId?: string;
  /** The server's reason, for the refusals that give one (e.g. APPROVAL_QUORUM_UNREACHABLE). */
  message?: string;
}

/**
 * Thin client for the action-service rules OPERATIONAL API (ADR-022 §12).
 *
 * Same-origin relative base like {@link ./actionApi} — deployment owns the reverse-proxy mapping and
 * the browser is never handed an action-service host or internal route. The whole surface is gated
 * behind the `rulesEnabled` feature flag (default false).
 */
const baseURL = '/api/actions';

const rulesClient: AxiosInstance = axios.create({ baseURL, timeout: 30000 });

/**
 * Simulation waits on ingestion, the zone service and a visual query in turn, so it gets longer
 * than the client's 30 s. action-service's `ActionVisualQueryTimeoutTest` reads this value and
 * checks the remote calls' worst case leaves headroom under it.
 */
export const SIMULATE_TIMEOUT_MILLIS = 40_000;

rulesClient.interceptors.request.use(async config => {
  const token = await getAuth().getToken();
  setBearerAuthorization(config.headers, token);
  return config;
});

rulesClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const request = error.config as (typeof error.config & { _retry?: boolean });
    if (error.response?.status === 401 && request && !request._retry) {
      try {
        await getAuth().refresh();
        request._retry = true;
        const token = await getAuth().getToken();
        setBearerAuthorization(request.headers, token);
        return rulesClient(request);
      } catch (refreshError) {
        notifySessionExpired();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

const RULES = '/api/v1/rules';
// The rule-match dead-letter admin surface EXISTS on this branch (RuleOutboxAdminController).
const DEAD_LETTERS = '/api/v1/rule-outbox/dead-letters';

export const rulesApi = {
  async list(signal?: AbortSignal): Promise<RuleListResponse> {
    const response = await rulesClient.get<RuleListResponse>(RULES, { signal });
    return response.data;
  },
  async detail(id: string, signal?: AbortSignal): Promise<RuleDetail> {
    const response = await rulesClient.get<RuleDetail>(`${RULES}/${encodeURIComponent(id)}`, { signal });
    return response.data;
  },
  /** The rule as last saved in the Pipeline Builder (tenant-admin). 409 when saved in more than one place. */
  async revision(id: string, signal?: AbortSignal): Promise<AuthoredRuleRevision> {
    const response = await rulesClient.get<AuthoredRuleRevision>(
      `${RULES}/${encodeURIComponent(id)}/revision`,
      { signal }
    );
    return response.data;
  },
  /** Every saved rule at its current revision (tenant-admin); `armed: false` lists the unarmed ones. */
  async revisions(
    params: { armed?: boolean } = {},
    signal?: AbortSignal
  ): Promise<AuthoredRuleRevisionListResponse> {
    const response = await rulesClient.get<AuthoredRuleRevisionListResponse>(`${RULES}/revisions`, {
      params,
      signal,
    });
    return response.data;
  },
  async arm(id: string, request: RuleArmRequest): Promise<void> {
    await rulesClient.post(`${RULES}/${encodeURIComponent(id)}/arm`, request);
  },
  async disarm(id: string, request: { reason?: string } = {}): Promise<void> {
    await rulesClient.post(`${RULES}/${encodeURIComponent(id)}/disarm`, request);
  },
  /** Retries revoking a disarmed rule's dataset access grant (tenant-admin). Always 200 with the outcome. */
  async retryGrantRevocation(id: string): Promise<GrantRevocationResult> {
    const response = await rulesClient.post<GrantRevocationResult>(
      `${RULES}/${encodeURIComponent(id)}/grant-revocation/retry`
    );
    return response.data;
  },
  async simulate(
    id: string,
    request: { ruleRevisionHash: string; window?: string },
    signal?: AbortSignal
  ): Promise<RuleSimulationReport> {
    const response = await rulesClient.post<RuleSimulationReport>(
      `${RULES}/${encodeURIComponent(id)}/simulate`,
      request,
      { signal, timeout: SIMULATE_TIMEOUT_MILLIS }
    );
    return response.data;
  },
  async setKillSwitch(
    id: string,
    request: { engaged: boolean; reason?: string }
  ): Promise<{ scope: string; engaged: boolean; epoch: number }> {
    const response = await rulesClient.post<{ scope: string; engaged: boolean; epoch: number }>(
      `${RULES}/${encodeURIComponent(id)}/kill-switch`,
      request
    );
    return response.data;
  },
  async tenantKillSwitch(signal?: AbortSignal): Promise<TenantKillSwitchView> {
    const response = await rulesClient.get<TenantKillSwitchView>(`${RULES}/kill-switch/tenant`, { signal });
    return response.data;
  },
  async setTenantKillSwitch(request: {
    engaged: boolean;
    reason: string;
  }): Promise<{ scope: string; engaged: boolean; epoch: number }> {
    const response = await rulesClient.post<{ scope: string; engaged: boolean; epoch: number }>(
      `${RULES}/kill-switch/tenant`,
      request
    );
    return response.data;
  },
  async rateCeiling(signal?: AbortSignal): Promise<RuleRateCeiling> {
    const response = await rulesClient.get<RuleRateCeiling>(`${RULES}/rate-ceiling`, { signal });
    return response.data;
  },
  async setRateCeiling(request: RuleRateCeilingRequest): Promise<RuleRateCeiling> {
    const response = await rulesClient.put<RuleRateCeiling>(`${RULES}/rate-ceiling`, request);
    return response.data;
  },
  async untripRateCeiling(request: { reason?: string }): Promise<RuleUntripResult> {
    const response = await rulesClient.post<RuleUntripResult>(`${RULES}/rate-ceiling/untrip`, request);
    return response.data;
  },
  async deadLetters(
    params: { limit?: number; cursor?: string } = {},
    signal?: AbortSignal
  ): Promise<RuleDeadLetterPage> {
    const response = await rulesClient.get<RuleDeadLetterPage>(DEAD_LETTERS, { params, signal });
    return response.data;
  },
  // Contract: RuleOutboxAdminModels.ReplayRequest requires { replayRequestId, reason }.
  async replayDeadLetter(
    outboxId: string,
    request: { replayRequestId: string; reason: string }
  ): Promise<RuleDeadLetter> {
    const response = await rulesClient.post<RuleDeadLetter>(
      `${DEAD_LETTERS}/${encodeURIComponent(outboxId)}/replay`,
      request
    );
    return response.data;
  },
  /**
   * True when the rules operational API is not reachable (endpoint not deployed yet, or the tenant
   * is not opted in). Callers render a "feature unavailable" state instead of an error banner.
   */
  isUnavailable(error: unknown): boolean {
    if (axios.isCancel(error)) {
      return false;
    }
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      // No response = network/proxy gap; 404/501 = endpoint absent; 403 = tenant not opted in.
      return status === undefined || status === 404 || status === 403 || status === 501;
    }
    return false;
  },
  /** Reads the `{error, correlationId}` body the Rules API returns; empty for non-HTTP failures. */
  failure(error: unknown): RuleApiFailure {
    if (!axios.isAxiosError(error)) {
      return {};
    }
    const data = error.response?.data as
      | { error?: unknown; correlationId?: unknown; message?: unknown }
      | undefined;
    return {
      status: error.response?.status,
      code: typeof data?.error === 'string' ? data.error : undefined,
      correlationId: typeof data?.correlationId === 'string' ? data.correlationId : undefined,
      message: typeof data?.message === 'string' ? data.message : undefined,
    };
  },
  isConflict(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 409;
  },
  isCanceled(error: unknown): boolean {
    return axios.isCancel(error);
  },
};

const fallbackUuid = (): string => {
  const segment = (length: number) => Math.random().toString(16).slice(2).padEnd(length, '0').slice(0, length);
  return `${segment(8)}-${segment(4)}-4${segment(3)}-8${segment(3)}-${segment(12)}`;
};

export const generateRequestId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : fallbackUuid();

export { rulesClient };
